/**
 * Property Repository
 * 
 * Extends BaseRepository with property-specific operations.
 * Common CRUD inherited from BaseRepository - eliminates ~200 lines of duplication!
 */

import { 
  writeBatch,
  doc,
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Property } from './Property';
import { BaseRepository } from './BaseRepository';

export class PropertyRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'discovery_properties', Property);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // (Common findById, findByIds, findAll inherited from BaseRepository)
  // ========================================

  /**
   * A home's version history, newest first (1 Nov 2026: read here, with no scraper in the path).
   * The versions live under the home's DOCUMENT id, which for a historical import is not its listing id
   * (EST-170). So the document is the one given, or else found by its `propertyId` FIELD; never
   * `doc(listingId)`. With no document found, an empty list.
   * @param {{ id?: string, propertyId?: string }} home
   * @param {number} max
   * @returns {Promise<{ versions: object[], more: boolean }>}
   */
  async findVersions(home, max = 10) {
    let docId = home && home.id;
    if (!docId && home && home.propertyId) {
      const found = await getDocs(query(collection(this.db, this.collectionName), where('propertyId', '==', String(home.propertyId)), firestoreLimit(1)));
      docId = found.empty ? null : found.docs[0].id;
    }
    if (!docId) return { versions: [], more: false };
    const snap = await getDocs(query(collection(this.db, this.collectionName, docId, 'versions'), orderBy('timestamp', 'desc'), firestoreLimit(max)));
    const versions = snap.docs.map((d) => {
      const v = d.data();
      return { id: d.id, ...v, timestamp: v.timestamp?.toDate?.()?.toISOString() || v.timestamp };
    });
    return { versions, more: snap.size >= max };
  }

  /**
   * Find properties by mailshot ID
   * @param {string} mailshotId - Mailshot ID
   * @returns {Promise<Property[]>} - Array of Property instances
   */
  async findByMailshotId(mailshotId) {
    return this.findAll({
      where: [['mailshotIds', 'array-contains', mailshotId]]
    });
  }

  /**
   * Find properties by scrape ID
   * @param {string} scrapeId - Scrape ID (jobId)
   * @returns {Promise<Property[]>} - Array of Property instances
   */
  async findByScrapeId(scrapeId) {
    return this.findAll({
      where: [['jobId', '==', scrapeId]]
    });
  }

  /**
   * Find duplicate properties by address hash
   * @param {string} addressHash - Address hash to search for
   * @returns {Promise<Property[]>} - Array of properties with same address hash
   */
  async findDuplicatesByAddressHash(addressHash) {
    return this.findAll({
      where: [['addressHash', '==', addressHash]]
    });
  }

  // ========================================
  // OVERRIDDEN CRUD METHODS
  // (Extending base functionality with custom logic)
  // ========================================

  /**
   * Create a new property (override to use propertyId as document ID)
   * @param {Property} property - Property instance
   * @returns {Promise<Property>} - Created property instance
   */
  async create(property) {
    // Validate before creating
    property.validate();
    
    const docData = property.toFirestore();
    
    // Add dateAdded timestamp for sorting (matches scraper behavior)
    const timestamp = serverTimestamp();
    docData.dateAdded = timestamp;
    docData.fetchedAt = timestamp;
    
    // Use propertyId as document ID (custom behavior)
    const createdProperty = await super.create(docData, property.propertyId);
    
    return createdProperty;
  }

  /**
   * Delete a property (override to check business rules and delegate to Cloud Function)
   * @param {string} id - Property ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    // Load property to check if it can be deleted
    const property = await this.findById(id);
    
    if (!property) {
      throw new Error(`Property ${id} not found`);
    }
    
    // Check business rules
    if (!property.canBeDeleted()) {
      const reason = property.getDeleteBlockReason();
      throw new Error(`Cannot delete property: ${reason}`);
    }
    
    // Delegate to Cloud Function for cascade delete
    const deletePropertyFn = httpsCallable(this.functions, 'deleteProperty');
    await deletePropertyFn({ propertyId: id });
  }

  // ========================================
  // CLOUD FUNCTION DELEGATIONS
  // (Complex multi-collection operations)
  // ========================================

  /**
   * Add property to mailshot (delegates to Cloud Function)
   * @param {string} propertyId - Property ID
   * @param {string} mailshotId - Mailshot ID
   * @returns {Promise<void>}
   */
  async addToMailshot(propertyId, mailshotId) {
    const addToMailshotFn = httpsCallable(this.functions, 'addPropertyToMailshot');
    await addToMailshotFn({ propertyId, mailshotId });
  }

  /**
   * Remove property from mailshot (delegates to Cloud Function)
   * @param {string} propertyId - Property ID
   * @param {string} mailshotId - Mailshot ID
   * @returns {Promise<void>}
   */
  async removeFromMailshot(propertyId, mailshotId) {
    const removeFromMailshotFn = httpsCallable(this.functions, 'removePropertyFromMailshot');
    await removeFromMailshotFn({ propertyId, mailshotId });
  }

  /**
   * Update property status (delegates to Cloud Function for cache updates)
   * @param {string} propertyId - Property ID
   * @param {string} newStatus - New status
   * @returns {Promise<void>}
   */
  async updateStatus(propertyId, newStatus) {
    const updateStatusFn = httpsCallable(this.functions, 'updatePropertyStatus');
    await updateStatusFn({ propertyId, status: newStatus });
  }

  /**
   * Merge properties (delegates to Cloud Function)
   * @param {string[]} sourceIds - Source property IDs to merge
   * @param {string} targetId - Target property ID
   * @returns {Promise<void>}
   */
  async merge(sourceIds, targetId) {
    // Validate merge is possible
    const [target, ...sources] = await this.findByIds([targetId, ...sourceIds]);
    
    if (!target) {
      throw new Error(`Target property ${targetId} not found`);
    }
    
    // Check if all source properties can be merged with target
    for (const source of sources) {
      if (!source) continue;
      if (!target.canBeMergedWith(source)) {
        throw new Error(`Cannot merge property ${source.id} with ${targetId}`);
      }
    }
    
    // Delegate to Cloud Function for atomic merge
    const mergePropertiesFn = httpsCallable(this.functions, 'mergeProperties');
    await mergePropertiesFn({ sourceIds, targetId });
  }

  /**
   * Update delivery status for multiple properties
   * @param {string[]} propertyIds - Array of property IDs
   * @param {string} deliveryStatus - New delivery status
   * @returns {Promise<void>}
   */
  async updateDeliveryStatus(propertyIds, deliveryStatus) {
    // Use batched writes for efficiency
    const batch = writeBatch(this.db);
    const timestamp = serverTimestamp();
    
    propertyIds.forEach(id => {
      const docRef = doc(this.db, this.collectionName, id);
      batch.update(docRef, {
        deliveryStatus,
        updatedAt: timestamp
      });
    });
    
    await batch.commit();
  }
}
